import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Mail, Phone, User, CreditCard, CheckCircle, XCircle, Loader, RefreshCw, LogOut, Check, X, Key } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, cancelled
  const [updatingBooking, setUpdatingBooking] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Check authentication
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('meya_admin_auth') === 'true';
    const loginTime = localStorage.getItem('meya_admin_login_time');
    
    // Session expire après 24h
    const isSessionValid = loginTime && (Date.now() - parseInt(loginTime)) < 24 * 60 * 60 * 1000;
    
    if (!isAuthenticated || !isSessionValid) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('meya_admin_auth');
    localStorage.removeItem('meya_admin_login_time');
    toast.success('Déconnexion réussie');
    navigate('/admin');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setChangingPassword(true);
    try {
      // Sauvegarder dans localStorage (à remplacer par un appel backend en production)
      localStorage.setItem('meya_admin_custom_password', newPassword);
      toast.success('Mot de passe changé avec succès !');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    setUpdatingBooking(bookingId);
    try {
      await axios.patch(`${BACKEND_URL}/api/bookings/${bookingId}/status`, {
        booking_status: newStatus
      });
      
      toast.success(`Réservation ${newStatus === 'confirmed' ? 'confirmée' : 'annulée'} !`);
      
      // Refresh bookings
      await fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingBooking(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'En attente', color: 'orange', icon: Clock },
      confirmed: { text: 'Confirmée', color: 'green', icon: CheckCircle },
      cancelled: { text: 'Annulée', color: 'red', icon: XCircle }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`status-badge status-${badge.color}`}>
        <Icon size={16} />
        {badge.text}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const badges = {
      pending: { text: 'En attente', color: 'orange' },
      paid: { text: 'Payé', color: 'green' },
      failed: { text: 'Échoué', color: 'red' }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`payment-badge payment-${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    return booking.booking_status === filter;
  });

  const stats = {
    total: bookings.length,
    confirmed: bookings.filter(b => b.booking_status === 'confirmed').length,
    pending: bookings.filter(b => b.booking_status === 'pending').length,
    totalRevenue: bookings
      .filter(b => b.payment_status === 'paid')
      .reduce((sum, b) => sum + (b.deposit_amount || 0), 0)
  };

  return (
    <div className="page-admin">
      {/* Hero Header */}
      <section className="admin-hero">
        <div className="service-hero-pattern" />
        <div className="admin-hero-content">
          <div className="admin-hero-top">
            <h1 className="admin-hero-title">Dashboard Admin</h1>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button 
                onClick={() => setShowPasswordModal(true)} 
                className="btn-change-password"
                title="Changer le mot de passe"
              >
                <Key size={16} />
              </button>
              <button onClick={handleLogout} className="btn-logout">
                <LogOut size={20} />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
          <p className="admin-hero-description">
            Gérez vos réservations et paiements
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="admin-stats-section">
        <div className="container-admin">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'var(--color-primary)' }}>
                <Calendar size={24} />
              </div>
              <div className="stat-info">
                <h3>Total Réservations</h3>
                <p className="stat-value">{stats.total}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#10b981' }}>
                <CheckCircle size={24} />
              </div>
              <div className="stat-info">
                <h3>Confirmées</h3>
                <p className="stat-value">{stats.confirmed}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#f59e0b' }}>
                <Clock size={24} />
              </div>
              <div className="stat-info">
                <h3>En Attente</h3>
                <p className="stat-value">{stats.pending}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'var(--color-gold)' }}>
                <CreditCard size={24} />
              </div>
              <div className="stat-info">
                <h3>Acomptes Reçus</h3>
                <p className="stat-value">{stats.totalRevenue}€</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bookings Table */}
      <section className="admin-bookings-section">
        <div className="container-admin">
          <div className="bookings-header">
            <h2>Liste des Réservations</h2>
            <div className="bookings-actions">
              <div className="filter-buttons">
                <button 
                  className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  Toutes
                </button>
                <button 
                  className={`filter-btn ${filter === 'confirmed' ? 'active' : ''}`}
                  onClick={() => setFilter('confirmed')}
                >
                  Confirmées
                </button>
                <button 
                  className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
                  onClick={() => setFilter('pending')}
                >
                  En attente
                </button>
              </div>
              <button className="refresh-btn" onClick={fetchBookings} disabled={loading}>
                <RefreshCw size={16} className={loading ? 'spin' : ''} />
                Actualiser
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <Loader className="spin" size={48} />
              <p>Chargement des réservations...</p>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="empty-state">
              <Calendar size={48} />
              <p>Aucune réservation pour le moment</p>
            </div>
          ) : (
            <div className="bookings-list">
              {filteredBookings.map((booking) => (
                <div key={booking.id} className="booking-card">
                  <div className="booking-card-header">
                    <div className="booking-service">
                      <h3>{booking.service}</h3>
                      {getStatusBadge(booking.booking_status)}
                    </div>
                    <div className="booking-payment">
                      {getPaymentBadge(booking.payment_status)}
                      <span className="booking-amount">{booking.deposit_amount}€</span>
                    </div>
                  </div>

                  <div className="booking-card-body">
                    <div className="booking-info-grid">
                      <div className="booking-info-item">
                        <Calendar size={18} />
                        <div>
                          <strong>Date</strong>
                          <p>{new Date(booking.date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</p>
                        </div>
                      </div>

                      <div className="booking-info-item">
                        <Clock size={18} />
                        <div>
                          <strong>Heure</strong>
                          <p>{booking.time}</p>
                        </div>
                      </div>

                      <div className="booking-info-item">
                        <User size={18} />
                        <div>
                          <strong>Client</strong>
                          <p>{booking.name}</p>
                        </div>
                      </div>

                      <div className="booking-info-item">
                        <Mail size={18} />
                        <div>
                          <strong>Email</strong>
                          <p>{booking.email}</p>
                        </div>
                      </div>

                      <div className="booking-info-item">
                        <Phone size={18} />
                        <div>
                          <strong>Téléphone</strong>
                          <p>{booking.phone}</p>
                        </div>
                      </div>

                      {booking.message && (
                        <div className="booking-info-item full-width">
                          <div>
                            <strong>Message</strong>
                            <p>{booking.message}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="booking-card-footer">
                    <span className="booking-date">
                      Réservé le {new Date(booking.created_at).toLocaleDateString('fr-FR')} à {new Date(booking.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    
                    {/* Action Buttons */}
                    <div className="booking-actions">
                      {booking.booking_status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                            disabled={updatingBooking === booking.id}
                            className="btn-action btn-confirm"
                            title="Confirmer manuellement"
                          >
                            <Check size={16} />
                            <span>Confirmer</span>
                          </button>
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                            disabled={updatingBooking === booking.id}
                            className="btn-action btn-cancel"
                            title="Annuler"
                          >
                            <X size={16} />
                            <span>Annuler</span>
                          </button>
                        </>
                      )}
                      {booking.booking_status === 'confirmed' && (
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                          disabled={updatingBooking === booking.id}
                          className="btn-action btn-cancel"
                          title="Annuler"
                        >
                          <X size={16} />
                          <span>Annuler</span>
                        </button>
                      )}
                      {booking.booking_status === 'cancelled' && (
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                          disabled={updatingBooking === booking.id}
                          className="btn-action btn-confirm"
                          title="Réactiver"
                        >
                          <Check size={16} />
                          <span>Réactiver</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Modal Changement de Mot de Passe */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Changer le mot de passe</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowPasswordModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="password-change-form">
              <div className="form-group">
                <label htmlFor="newPassword">Nouveau mot de passe</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="form-input"
                  placeholder="Au moins 6 caractères"
                  required
                  minLength={6}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-input"
                  placeholder="Retapez le mot de passe"
                  required
                  minLength={6}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="btn-secondary"
                  disabled={changingPassword}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={changingPassword || !newPassword || !confirmPassword}
                >
                  {changingPassword ? 'Modification...' : 'Modifier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
